import * as React from 'react';
import { cn } from '../utils';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortIndicatorProps {
  /**
   * Current sort direction for this column
   * null = unsorted, 'asc' = ascending, 'desc' = descending
   */
  direction: SortDirection;
  /**
   * Whether this column is currently the active sort column
   */
  isActive?: boolean;
  /**
   * Size variant for the indicator
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for screen readers
   */
  'aria-label'?: string;
}

const sizeClasses = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5', 
  lg: 'w-4 h-4'
} as const;

export const SortIndicator = React.forwardRef<HTMLDivElement, SortIndicatorProps>(
  ({ direction, isActive = false, size = 'md', className, 'aria-label': ariaLabel, ...props }, ref) => {
    const iconSize = sizeClasses[size];
    
    // Determine visual state based on active status and direction
    const getStateClasses = () => {
      if (isActive && direction) {
        return 'text-brand-600 opacity-100';
      }
      return 'text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200';
    };

    const renderIcon = () => {
      if (direction === 'asc') {
        return (
          <svg
            className={cn(iconSize, 'transition-all duration-200 ease-out')}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        );
      }
      
      if (direction === 'desc') {
        return (
          <svg
            className={cn(iconSize, 'transition-all duration-200 ease-out')}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        );
      }
      
      // Unsorted state - dual chevrons
      return (
        <svg
          className={cn(iconSize, 'transition-all duration-200 ease-out')}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 15l-4 4-4-4" />
        </svg>
      );
    };

    // Screen reader text
    const getSortStatus = () => {
      if (!isActive || !direction) return 'Not sorted';
      return direction === 'asc' ? 'Sorted ascending' : 'Sorted descending';
    };

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center flex-shrink-0 ml-1',
          getStateClasses(),
          className
        )}
        aria-label={ariaLabel || getSortStatus()}
        role="img"
        {...props}
      >
        {renderIcon()}
      </div>
    );
  }
);

SortIndicator.displayName = 'SortIndicator';

/**
 * Hook for managing sort state with type safety
 */
export function useSortState<T extends string>(initialSort?: T) {
  const [sortField, setSortField] = React.useState<T | null>(initialSort || null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);

  const handleSort = React.useCallback((field: T) => {
    if (sortField === field) {
      // Cycle through: null -> asc -> desc -> null
      if (sortDirection === null) {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const getSortProps = React.useCallback((field: T) => ({
    direction: sortField === field ? sortDirection : null,
    isActive: sortField === field,
  }), [sortField, sortDirection]);

  return {
    sortField,
    sortDirection,
    handleSort,
    getSortProps,
  };
}